import type { ColumnDef } from '@tanstack/react-table'
import { Badge } from '@/components/ui/badge'
import { DataTableColumnHeader } from '@/components/data-table'
import { formatDateTime, formatLabel } from '@/lib/format'
import { type AdminSongRow, songOwnerLabel } from '../../types/song-actions'

export type { AdminSongRow }

const STATUS_OPTIONS = [
  { label: 'Live', value: 'live' },
  { label: 'Deleted', value: 'deleted' },
] as const

const VISIBILITY_OPTIONS = [
  { label: 'Public', value: 'public' },
  { label: 'Band private', value: 'band_private' },
  { label: 'Draft', value: 'draft' },
] as const

export const ADMIN_SONG_SORT_IDS = ['title', 'createdAt', 'deletedAt'] as const

export const ADMIN_SONG_FILTER_KEYS = ['status', 'visibility'] as const

export function createAdminSongsColumns(): ColumnDef<AdminSongRow, unknown>[] {
  return [
    {
      id: 'title',
      accessorKey: 'title',
      meta: { label: 'Title' },
      enableSorting: true,
      enableHiding: false,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Title" />
      ),
      cell: ({ row }) => (
        <div className="min-w-0 max-w-[16rem]">
          <div className="truncate text-sm font-medium">
            {row.original.title}
          </div>
          <div className="truncate font-mono text-xs text-muted-foreground">
            {row.original.slug}
          </div>
        </div>
      ),
    },
    {
      id: 'artist',
      accessorKey: 'artist_name',
      meta: { label: 'Artist' },
      enableSorting: false,
      enableHiding: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Artist" />
      ),
      cell: ({ row }) => (
        <span className="block max-w-[12rem] truncate text-sm">
          {row.original.artist_name ?? '—'}
        </span>
      ),
    },
    {
      id: 'owner',
      accessorKey: 'owner_id',
      meta: { label: 'Owner' },
      enableSorting: false,
      enableHiding: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Owner" />
      ),
      cell: ({ row }) => (
        <span className="block max-w-[12rem] truncate text-sm text-muted-foreground">
          {songOwnerLabel(row.original)}
        </span>
      ),
    },
    {
      id: 'visibility',
      accessorKey: 'visibility',
      meta: {
        label: 'Visibility',
        variant: 'select',
        options: [...VISIBILITY_OPTIONS],
      },
      enableSorting: false,
      enableHiding: true,
      enableColumnFilter: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Visibility" />
      ),
      cell: ({ row }) => (
        <Badge variant="secondary" className="font-normal">
          {formatLabel(row.original.visibility)}
        </Badge>
      ),
    },
    {
      id: 'status',
      accessorKey: 'deleted_at',
      meta: {
        label: 'Status',
        variant: 'select',
        options: [...STATUS_OPTIONS],
      },
      enableSorting: false,
      enableHiding: true,
      enableColumnFilter: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Status" />
      ),
      cell: ({ row }) =>
        row.original.deleted_at ? (
          <Badge variant="destructive" className="font-normal">
            Deleted
          </Badge>
        ) : (
          <Badge variant="outline" className="font-normal">
            Live
          </Badge>
        ),
    },
    {
      id: 'createdAt',
      accessorKey: 'created_at',
      meta: { label: 'Created' },
      enableSorting: true,
      enableHiding: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Created" />
      ),
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-sm text-muted-foreground">
          {formatDateTime(row.original.created_at)}
        </span>
      ),
    },
  ]
}
